// ============================================================================
// api_webrtcPeerConnection.ts
// WebRTC — Factory PeerConnection, négociation Offer/Answer, candidats ICE
// v3.0 — Perfect Negotiation impolite peer (régie) + mutex anti-double-Offer
//         Logs ICE/SDP enrichis
// v3.1 — Fix reconnexion : onconnectionstatechange et watchdog vérifient que
//         la PC qui déclenche l'événement est bien celle enregistrée dans
//         remoteConnectionsRef avant de nettoyer (évite d'écraser une PC plus
//         récente créée lors d'un device_reconnected).
// ============================================================================

import { SignalingRefs, SignalingCallbacks } from '../../dataTypes/liveStreamManagment/types_signaling';
import { forceSdpCodecs, extractCodecFromSdp, parseSdpCodec } from '../../dataTypes/liveStreamManagment/utils_sdp';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';

import { cleanupDeviceBuffers, flushIceCandidateBuffer, notePcCloseCause, readPcCloseCause, trackPeerConnection, closeStrayPeerConnections } from './api_webrtcBuffers';

// ============================================================================
// SECTION 0 — Helpers de log ICE et SDP
// ============================================================================

const summarizeIceCandidate = (candidate: RTCIceCandidate | RTCIceCandidateInit | null | undefined): string => {
  if (!candidate) return '(fin gathering — null candidate)';
  const raw = (candidate as RTCIceCandidate).candidate
           ?? (candidate as RTCIceCandidateInit).candidate
           ?? '';
  if (!raw) return '(fin gathering — null candidate)';

  const parts    = raw.split(' ');
  const protocol = parts[2]?.toLowerCase() ?? '?';
  const address  = parts[4] ?? '?';
  const port     = parts[5] ?? '?';
  const prio     = parts[3] ?? '?';
  const typIdx   = parts.indexOf('typ');
  const type     = typIdx >= 0 ? parts[typIdx + 1] : '?';

  let extra = '';
  if (type === 'relay' || type === 'srflx') {
    const raddrIdx = parts.indexOf('raddr');
    const rportIdx = parts.indexOf('rport');
    if (raddrIdx >= 0 && rportIdx >= 0) {
      extra = ` (via ${parts[raddrIdx + 1]}:${parts[rportIdx + 1]})`;
    }
  }

  return `${type} ${protocol} ${address}:${port}${extra} [prio=${prio}]`;
};

const summarizeSdp = (sdp: string | undefined | null, label: string): string => {
  if (!sdp) return `${label} — SDP vide`;

  const lines = sdp.split('\n');

  const videoCodecs: string[] = [];
  const audioCodecs: string[] = [];
  lines.forEach(l => {
    const m = l.match(/^a=rtpmap:\d+ ([^/]+)/);
    if (!m) return;
    const codec = m[1].toUpperCase();
    if (['H264', 'VP8', 'VP9', 'AV1', 'H265'].includes(codec)) videoCodecs.push(codec);
    else if (['OPUS', 'PCMU', 'PCMA', 'G722', 'ISAC'].includes(codec)) audioCodecs.push(codec);
  });

  const h264Profiles: string[] = [];
  lines.forEach(l => {
    const m = l.match(/profile-level-id=([0-9a-fA-F]{6})/);
    if (m) {
      const pid         = m[1].toLowerCase();
      const profileByte = pid.slice(0, 2);
      const levelByte   = parseInt(pid.slice(4, 6), 16);
      const level       = `${Math.floor(levelByte / 10)}.${levelByte % 10}`;
      const name        = profileByte === '42' ? 'Baseline'
                        : profileByte === '4d' ? 'Main'
                        : profileByte === '64' ? 'High'
                        : `0x${profileByte}`;
      const entry = `${name} ${level} (${pid})`;
      if (!h264Profiles.includes(entry)) h264Profiles.push(entry);
    }
  });

  const directions: string[] = [];
  lines.forEach(l => {
    if (l.startsWith('a=sendrecv'))       directions.push('sendrecv');
    else if (l.startsWith('a=sendonly'))  directions.push('sendonly');
    else if (l.startsWith('a=recvonly'))  directions.push('recvonly');
    else if (l.startsWith('a=inactive'))  directions.push('inactive');
  });

  const mlines         = lines.filter(l => l.startsWith('m=')).map(l => l.split(' ')[0].slice(2));
  const ufragLine      = lines.find(l => l.startsWith('a=ice-ufrag:'));
  const ufrag          = ufragLine ? ufragLine.slice('a=ice-ufrag:'.length).trim() : '?';
  const hasFingerprint = lines.some(l => l.startsWith('a=fingerprint:'));
  const inlineCands    = lines.filter(l => l.startsWith('a=candidate:')).length;

  const videoStr  = videoCodecs.length
    ? Array.from(new Set(videoCodecs)).join(',') + (h264Profiles.length ? ` [${h264Profiles.join(' | ')}]` : '')
    : 'aucun';
  const audioStr  = audioCodecs.length ? Array.from(new Set(audioCodecs)).join(',') : 'aucun';
  const dirStr    = Array.from(new Set(directions)).join('/') || '?';
  const mlinesStr = mlines.join('+') || '?';
  const candStr   = inlineCands > 0 ? ` | ${inlineCands} candidats inline` : '';

  return `${label} | m-lines:${mlinesStr} dir:${dirStr} | vidéo:${videoStr} | audio:${audioStr} | ufrag:${ufrag}${candStr} | dtls:${hasFingerprint ? '✅' : '❌'}`;
};

// ============================================================================
// SECTION 11 — Mutex anti-double-négociation
// ============================================================================

const negotiationsInProgress = new Map<string, boolean>();

/**
 * Génération de négociation, par device.
 *
 * Une négociation dure : `connectToDevice` attend le gathering ICE complet, soit
 * jusqu'à 6 s. Pendant ce temps le device peut être PARTI et REVENU — c'est le
 * cas normal d'une passation de régie, où la nouvelle porteuse offre à un
 * appareil que le serveur vient d'inviter à refaire sa session.
 *
 * Deux dégâts alors, tous deux constatés en production le 25/08/2026 :
 *
 *   1. le mutex ci-dessus est encore tenu par la négociation en vol, et la vraie
 *      négociation — celle déclenchée par `device_reconnected` — est ÉCARTÉE.
 *      Elle n'est jamais rejouée : la régie garde un appareil « Connecté » dont
 *      elle ne verra jamais l'image (le retour, lui, passe : il n'emprunte pas
 *      ce chemin, d'où un device qui a son retour et reste invisible en régie) ;
 *   2. la négociation en vol finit par offrir depuis une PeerConnection fermée
 *      entre-temps, pour une session qui n'existe plus.
 *
 * D'où ce compteur : `invalidateDeviceNegotiation` l'incrémente, et toute
 * négociation dont la génération n'est plus la bonne s'arrête au lieu d'émettre.
 */
const negotiationGeneration = new Map<string, number>();

/**
 * Génération à laquelle appartient la PeerConnection actuellement enregistrée.
 *
 * Une PC survit à la session qui l'a créée : quand le device s'en va, la régie
 * met **10 à 20 s** à s'en apercevoir (ICE), et pendant tout ce temps
 * `connectionState` vaut encore `connected`. Une régie qui REPREND la main dans
 * cette fenêtre garde donc une PC qui se dit vivante alors que son correspondant
 * n'existe plus — et `connectToDevice` la respecte, décline poliment, et
 * l'appareil ne revient jamais à l'écran.
 *
 * C'est ce qui distinguait la première passation (régie sans passé, tout marche)
 * de la seconde (la régie retrouve la main, et son fantôme la bloque).
 */
const pcGeneration = new Map<string, number>();

/**
 * Déclare périmée toute négociation en cours pour ce device, et libère le mutex.
 *
 * À appeler quand le device ANNONCE une nouvelle session (`device_connected`,
 * `device_reconnected`) : ce qui se négociait visait la session d'avant. C'est
 * une supplantation, pas une annulation — l'appelant enchaîne sur la
 * négociation neuve, qui doit pouvoir prendre le mutex immédiatement.
 */
export const invalidateDeviceNegotiation = (device_id: string, raison: string): void => {
  negotiationGeneration.set(device_id, (negotiationGeneration.get(device_id) ?? 0) + 1);
  if (negotiationsInProgress.delete(device_id)) {
    console.warn(`[PC] 🚫 ${device_id}: négociation en vol supplantée (${raison})`);
  }
};

// ============================================================================
// SECTION 12 — Factory PeerConnection
// ============================================================================

/**
 * Appareils dont on a cessé d'attendre le retour.
 *
 * Le chien de garde ci-dessous rappelle `onWatchdogRetry` toutes les 20 s tant que
 * la PeerConnection ne se connecte pas, et chaque tentative refabrique une PC qui
 * récolte des candidats ICE et les envoie à un `target` que le serveur ne connaît
 * plus. Le 2026-08-28, oriane-opo est partie à 21:57 et la régie lui a tiré dessus
 * jusqu'à 01:10 : plus de cent `target_not_found` en trois heures, pour un téléphone
 * que rien ne pouvait ramener.
 *
 * Ce n'est pas au chien de garde de décider quand renoncer — il ne sait rien de
 * l'appareil, seulement de sa PC. La décision vient de la régie, qui tient le délai
 * de grâce, et se pose ici.
 */
const abandonedDevices = new Set<string>();

/** Plus de relance pour cet appareil, jusqu'à ce qu'il se réannonce. */
export const abandonDeviceNegotiation = (device_id: string): void => {
  abandonedDevices.add(device_id);
};

/** L'appareil est de retour : le chien de garde reprend son office. */
export const resumeDeviceNegotiation = (device_id: string): void => {
  abandonedDevices.delete(device_id);
};

export interface MakePeerConnectionParams {
  device_id: string;
  refs:      SignalingRefs;
  callbacks: SignalingCallbacks;
  onWatchdogRetry: (device_id: string) => Promise<void>;
}

export const makePeerConnection = ({
  device_id,
  refs,
  callbacks,
  onWatchdogRetry,
}: MakePeerConnectionParams): RTCPeerConnection => {
  refs.iceCandidateBufferRef.current.set(device_id, []);
  refs.remoteDescSetRef.current.set(device_id, false);

  console.log(`[PC] 🔧 Création PeerConnection pour ${device_id}`);
  const pc = new RTCPeerConnection(defaultRTCConfig());
  trackPeerConnection(pc, 'montant', device_id);

  callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔧 PeerConnection créée pour ${device_id}`, deviceId: device_id });

  // ── DataChannel keepalive ─────────────────────────────────────────────────
  const keepaliveDc = pc.createDataChannel('keepalive', { ordered: false, maxRetransmits: 0 });
  let keepaliveInterval: ReturnType<typeof setInterval> | null = null;

  keepaliveDc.onopen = () => {
    console.log(`[PC] 💓 Keepalive DC ouvert pour ${device_id}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `💓 Keepalive DC ouvert pour ${device_id}`, deviceId: device_id });
    keepaliveInterval = setInterval(() => {
      if (keepaliveDc.readyState === 'open') {
        try { keepaliveDc.send('ping'); } catch { /* silencieux */ }
      }
    }, 5000);
  };

  keepaliveDc.onclose = () => {
    if (keepaliveInterval) { clearInterval(keepaliveInterval); keepaliveInterval = null; }
    console.log(`[PC] 💓 Keepalive DC fermé pour ${device_id}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `💓 Keepalive DC fermé pour ${device_id}`, deviceId: device_id });
  };

  // ── Tracks entrants ───────────────────────────────────────────────────────
  pc.ontrack = (event) => {
    const track = event.track;
    const streamId = event.streams[0]?.id ?? 'UNDEFINED';
    console.log(`[PC] 📹 Track reçu de ${device_id}: ${track.kind} (${track.label || 'sans label'}) readyState=${track.readyState} muted=${track.muted} streams[0]=${streamId}`);
    callbacks.addSdpLog({ direction: 'received', type: 'event', label: `📹 Track reçu de ${device_id}: ${track.kind} (streams[0]=${streamId})`, deviceId: device_id });

    // Un mute précède souvent la fin de piste (média qui cesse d'arriver avant
    // que le transport ne tombe) : même contexte que onended, pour pouvoir lire
    // la séquence complète.
    track.onmute = () => {
      const detail = `pc: connection=${pc.connectionState} ice=${pc.iceConnectionState}`
        + ` | onglet=${typeof document !== 'undefined' ? document.visibilityState : '?'}`;
      console.warn(`[PC] 🔇 Track ${track.kind} MUTED silencieusement pour ${device_id} — ${detail}`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔇 Track ${track.kind} muté — ${device_id} — ${detail}`, deviceId: device_id });
    };
/*
    track.onunmute = () => {
      console.log(`[PC] 🔊 Track ${track.kind} UNMUTED pour ${device_id}`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔊 Track ${track.kind} démuté — ${device_id}`, deviceId: device_id });

      if (track.kind === 'video') {
        const conn = refs.remoteConnectionsRef.current.get(device_id);
        if (conn) {
          const receivers = pc.getReceivers();
          const videoTrack = receivers.find(r => r.track.kind === 'video')?.track;
          const audioTrack = receivers.find(r => r.track.kind === 'audio')?.track;
          const freshStream = new MediaStream([
            ...(videoTrack ? [videoTrack] : []),
            ...(audioTrack ? [audioTrack] : []),
          ]);
          console.log(`[PC] 🔊 onunmute video ${device_id}: freshStream tracks=[${freshStream.getTracks().map(t => t.kind + ':' + t.readyState).join(', ')}]`);
          callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔊 onunmute: freshStream [${freshStream.getTracks().map(t => t.kind + ':' + t.readyState).join(', ')}]`, deviceId: device_id });
          callbacks.setRemoteConnections(prev => {
            const next = new Map(prev);
            const existing = next.get(device_id);
            if (existing) {
              next.set(device_id, { ...existing, stream: freshStream });
              refs.remoteConnectionsRef.current.set(device_id, { ...existing, stream: freshStream });
            }
            return next;
          });
        }
      }
    };
*/

track.onunmute = () => {
      console.log(`[PC] 🔊 Track ${track.kind} UNMUTED pour ${device_id}`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔊 Track ${track.kind} démuté — ${device_id}`, deviceId: device_id });

      if (track.kind === 'video') {
        // C'est ICI, et NULLE PART AILLEURS, que le flux d'un appareil entre
        // dans le registry : sans cette injection, la mosaïque et le deck
        // n'auront jamais son image, quelle que soit la santé de la PC.
        //
        // L'ancienne version ne faisait rien quand `remoteConnections` n'avait
        // pas encore (ou plus) d'entrée pour ce device — et rien ne la rejouait.
        // Le média arrivait, personne ne le recevait. On n'exige donc plus
        // l'entrée : on la (re)pose. La SEULE raison de renoncer est que cette
        // PC ait été supplantée entre-temps par une plus récente.
        const enregistree = refs.remoteConnectionsRef.current.get(device_id);
        if (enregistree && enregistree.peerConnection !== pc) {
          console.warn(`[PC] 🔇 onunmute ${device_id} ignoré — cette PC a été supplantée par une plus récente`);
          return;
        }

        const receivers = pc.getReceivers();
        const videoTrack = receivers.find(r => r.track.kind === 'video')?.track;
        const audioTrack = receivers.find(r => r.track.kind === 'audio')?.track;
        const freshStream = new MediaStream([
          ...(videoTrack ? [videoTrack] : []),
          ...(audioTrack ? [audioTrack] : []),
        ]);
        if (!enregistree) {
          console.warn(`[PC] 🩹 onunmute ${device_id}: aucune entrée remoteConnections — elle est reposée ici`);
        }
        console.log(`[PC] 🔊 onunmute video ${device_id}: freshStream tracks=[${freshStream.getTracks().map(t => t.kind + ':' + t.readyState).join(', ')}]`);
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔊 onunmute: freshStream [${freshStream.getTracks().map(t => t.kind + ':' + t.readyState).join(', ')}]`, deviceId: device_id });

        const conn = { device_id, peerConnection: pc, stream: freshStream };
        refs.remoteConnectionsRef.current.set(device_id, conn);
        callbacks.setRemoteConnections(prev => {
          const next = new Map(prev);
          next.set(device_id, conn);
          return next;
        });

        // Injecter le nouveau stream dans le registry pour que le canvas
        // et les previews basculent immédiatement sur la nouvelle caméra
        if (callbacks.injectStream) {
          console.log(`[PC] 🔊 onunmute: injectStream remote-${device_id} → freshStream`);
          callbacks.injectStream(`remote-${device_id}`, freshStream);
        }
      }
    };


    // Une piste reçue ne se termine que si la PeerConnection est fermée, si le
    // device a arrêté d'émettre, ou si le transport est tombé. Le message seul
    // ne permet pas de trancher : on lui joint le motif de fermeture enregistré
    // par les sites de `pc.close()` (readPcCloseCause dit explicitement quand
    // AUCUNE fermeture régie n'est en cause) et l'état complet de la PC.
    track.onended = () => {
      const cause     = readPcCloseCause(device_id);
      const isCurrent = refs.remoteConnectionsRef.current.get(device_id)?.peerConnection === pc;
      const detail =
        `cause: ${cause}`
        + ` | pc: connection=${pc.connectionState} ice=${pc.iceConnectionState} signaling=${pc.signalingState}`
        + ` | pcCourante=${isCurrent ? 'oui' : 'non (déjà remplacée)'}`
        + ` | track: muted=${track.muted} enabled=${track.enabled}`
        + ` | onglet=${typeof document !== 'undefined' ? document.visibilityState : '?'}`;
      console.error(`[PC] ❌ Track ${track.kind} ENDED silencieusement pour ${device_id} — ${detail}`);
      callbacks.addSdpLog({
        direction: 'received', type: 'error',
        label: `❌ Track ${track.kind} ended silencieusement — ${device_id} — ${detail}`,
        deviceId: device_id,
      });
    };

    if (track.kind === 'video') {
      let stream = event.streams[0] ?? null;
      if (!stream) {
        const receivers = pc.getReceivers();
        const videoTrack = receivers.find(r => r.track.kind === 'video')?.track;
        const audioTrack = receivers.find(r => r.track.kind === 'audio')?.track;
        stream = new MediaStream([
          ...(videoTrack ? [videoTrack] : []),
          ...(audioTrack ? [audioTrack] : []),
        ]);
        console.log(`[PC] 📹 ontrack video ${device_id}: event.streams[0] absent — stream reconstruit depuis getReceivers()`);
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `📹 ontrack: stream reconstruit depuis getReceivers() (streams[0] absent)`, deviceId: device_id });
      }
      // L'entrée doit décrire CETTE PC. La greffer sur celle d'une autre —
      // le fantôme d'une session précédente, par exemple — donnerait une entrée
      // qui porte le flux neuf sous une PeerConnection morte : c'est alors la
      // mort de la morte qui efface l'appareil de la mosaïque.
      const enregistree = refs.remoteConnectionsRef.current.get(device_id);
      if (enregistree && enregistree.peerConnection !== pc) {
        console.warn(`[PC] 🔇 ontrack ${device_id} ignoré — cette PC a été supplantée par une plus récente`);
        return;
      }
      const updatedConn = { device_id, peerConnection: pc, stream };
      refs.remoteConnectionsRef.current.set(device_id, updatedConn);
      callbacks.setRemoteConnections(prev => {
        const next = new Map(prev);
        next.set(device_id, updatedConn);
        return next;
      });

      // Le registre reçoit le flux ICI, et plus seulement au démutage.
      //
      // Ce qu'on répare (Nasa TV, 25/08/2026) : à une renégociation — passation de
      // régie, reconnexion du téléphone — l'ancien flux restait dans le registre si
      // `unmute` ne se rejouait pas. Or un flux distant orphelin garde des pistes
      // `live` et NON mutées : rien ne le distingue d'un flux vivant. La mosaïque y
      // montrait une image figée, et l'enregistrement multi-angle publiait cette
      // piste morte — MediaMTX ouvrait la session WHIP, aucune image n'arrivait,
      // l'angle était abandonné au bout de 4 s. Un smartphone manquait à la prise,
      // sans une erreur nulle part.
      //
      // Le démutage continue de réinjecter derrière : il reconstruit le flux depuis
      // les receivers, ce qui rattrape un remplacement de piste en cours de session.
      if (callbacks.injectStream) {
        console.log(`[PC] 📹 ontrack: injectStream remote-${device_id} → flux de la session en cours`);
        callbacks.injectStream(`remote-${device_id}`, stream);
        // L'image est là : la place tenue devient une place occupée. Le WHEP a son
        // propre appel — en P2P la piste ne passe pas par lui.
        callbacks.clearDeviceAwaiting?.(device_id);
      }
    }

    // La piste audio n'est PAS lue ici. Elle voyage dans le stream de la source
    // `remote-<id>` et n'est rendue audible que par le graphe Web Audio
    // (AudioSourceRegistry, via connectAudioForSource qui crée déjà pour les
    // sources `remote` son propre <audio> muet — le puits que Chromium exige
    // pour alimenter createMediaStreamSource). Un <audio> à volume 1 ici sortait
    // en amont de tout le graphe : ni le Switch audio, ni les mutes de la régie,
    // ni l'intercomm ne pouvaient le couper, et le device s'entendait dès sa
    // connexion.
  };

  // ── Watchdog ──────────────────────────────────────────────────────────────
  // v3.1 : vérifie que cette PC est toujours la PC courante avant de retry
  let watchdogTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    if (pc.connectionState !== 'connected') {
      // Si une PC plus récente a été créée pour ce device, ignorer ce watchdog
      const current = refs.remoteConnectionsRef.current.get(device_id);
      if (current?.peerConnection !== pc) {
        console.log(`[PC] ⏱ Watchdog ${device_id}: PC remplacée — watchdog ignoré`);
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⏱ Watchdog ${device_id}: PC remplacée — ignoré`, deviceId: device_id });
        return;
      }
      console.warn(`[PC] ⏱ Watchdog ${device_id}: pas connecté après 20s (état: ${pc.connectionState}) → retry`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⏱ Watchdog ${device_id}: pas connecté après 20s — retry`, deviceId: device_id });
      notePcCloseCause(device_id, `watchdog 20 s — PC non connectée (état: ${pc.connectionState})`);
      pc.close();
      cleanupDeviceBuffers(device_id, refs);
      callbacks.setRemoteConnections(prev => { const next = new Map(prev); next.delete(device_id); return next; });
      setTimeout(() => { if (!abandonedDevices.has(device_id)) onWatchdogRetry(device_id); }, 500);
    }
  }, 20000);

  // ── ICE sortants ──────────────────────────────────────────────────────────
  pc.onicecandidate = (event) => {
    if (event.candidate && refs.wsRef.current?.readyState === WebSocket.OPEN) {
      const summary = summarizeIceCandidate(event.candidate);
      console.log(`[PC] 🧊 ICE sortant → ${device_id}: ${summary}`);
      callbacks.addSdpLog({ direction: 'sent', type: 'event', label: `🧊 ICE → ${device_id}: ${summary}`, deviceId: device_id });
      refs.wsRef.current.send(JSON.stringify({ type: 'ice-candidate', target: device_id, payload: event.candidate }));
    } else if (!event.candidate) {
      console.log(`[PC] 🧊 ICE gathering terminé pour ${device_id}`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🧊 ICE gathering terminé pour ${device_id}`, deviceId: device_id });
    }
  };

  // ── État de connexion ─────────────────────────────────────────────────────
  // v3.1 : vérifie que cette PC est toujours la PC courante avant de nettoyer
  pc.onconnectionstatechange = () => {
    const state = pc.connectionState;
    console.log(`[PC] 🔗 ${device_id}: connectionState → ${state}`);

    if (state === 'connected') {
      if (watchdogTimer !== null) {
        console.log(`[PC] ✅ ${device_id}: connecté — watchdog annulé`);
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
      pc.getStats().then(stats => {
        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
            let localInfo  = '';
            let remoteInfo = '';
            stats.forEach(r => {
              if (r.id === report.localCandidateId)  localInfo  = `${r.candidateType} ${r.protocol} ${r.address}:${r.port}`;
              if (r.id === report.remoteCandidateId) remoteInfo = `${r.candidateType} ${r.protocol} ${r.address}:${r.port}`;
            });
            const pairSummary = `local=[${localInfo}] remote=[${remoteInfo}]`;
            console.log(`[PC] ✅ Paire ICE nominée pour ${device_id}: ${pairSummary}`);
            callbacks.addSdpLog({ direction: 'info', type: 'event', label: `✅ Paire ICE nominée: ${pairSummary}`, deviceId: device_id });
          }
        });
      }).catch(() => {/* silencieux */});
    }

    if (state === 'failed' || state === 'disconnected' || state === 'closed') {
      // Contexte au moment de la chute : l'état de l'onglet départage une coupure
      // réseau d'un onglet suspendu par le navigateur (Chrome/Mac en arrière-plan),
      // et le motif de fermeture dit si la régie est à l'origine de ce `closed`.
      console.warn(
        `[PC] 🔻 ${device_id}: connexion ${state}`
        + ` | ice=${pc.iceConnectionState} signaling=${pc.signalingState}`
        + ` | onglet=${typeof document !== 'undefined' ? document.visibilityState : '?'}`
        + ` | réseau=${typeof navigator !== 'undefined' && navigator.onLine ? 'en ligne' : 'hors ligne'}`
        + ` | ${readPcCloseCause(device_id)}`
      );

      if (keepaliveInterval) { clearInterval(keepaliveInterval); keepaliveInterval = null; }
      if (watchdogTimer !== null) { clearTimeout(watchdogTimer); watchdogTimer = null; }

      // v3.1 : ne nettoyer remoteConnections que si c'est bien CETTE PC
      // qui est enregistrée — pas une PC plus récente créée lors d'une
      // reconnexion (device_reconnected crée une nouvelle PC avant que
      // l'ancienne ait fini de se fermer).
      const current = refs.remoteConnectionsRef.current.get(device_id);
      if (current?.peerConnection === pc) {
        refs.remoteConnectionsRef.current.delete(device_id);
        callbacks.setRemoteConnections(prev => {
          const next = new Map(prev);
          next.delete(device_id);
          return next;
        });
        cleanupDeviceBuffers(device_id, refs);

        if (state === 'failed') {
          console.warn(`[PC] 🔄 ${device_id}: PC failed → reconnexion auto dans 2s`);
          callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔄 ${device_id}: reconnexion automatique dans 2s…`, deviceId: device_id });
          // Fermer AVANT de reconnecter : la tentative suivante crée une PC neuve,
          // celle-ci est condamnée. Sans ça elle restait ouverte et inventoriée
          // pour toujours — même fuite que celle constatée sur le WHEP le
          // 27/08/2026, avec le même symptôme : l'appareil apparaît autant de fois
          // dans les débits qu'il s'est reconnecté.
          //
          // Uniquement sur `failed`. Un `disconnected` se rétablit de lui-même et
          // ne déclenche aucune reconnexion ici : le fermer condamnerait un lien
          // qui allait revenir.
          try { pc.close(); } catch { /* déjà fermée */ }
          setTimeout(() => { if (!abandonedDevices.has(device_id)) onWatchdogRetry(device_id); }, 2000);
        }
      } else {
        console.warn(`[PC] 🔇 ${device_id}: state=${state} ignoré — PC remplacée par une plus récente`);
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔇 ${device_id}: ${state} ignoré — PC remplacée`, deviceId: device_id });
        // Remplacée ET morte : plus personne ne la fermera, puisque le chemin
        // ci-dessus ne s'occupe que de la PC en cours.
        if (state === 'failed') { try { pc.close(); } catch { /* déjà fermée */ } }
      }
    }

    const isErr = state === 'failed' || state === 'disconnected';
    callbacks.addSdpLog({ direction: isErr ? 'received' : 'info', type: isErr ? 'error' : 'event', label: `🔗 ${device_id}: ${state}`, deviceId: device_id });

    const existing = refs.remoteDeviceInfoRef.current.get(device_id) || { device_id, label: device_id };
    refs.remoteDeviceInfoRef.current.set(device_id, { ...existing, connectionState: state, iceState: pc.iceConnectionState });
  };

  pc.oniceconnectionstatechange = () => {
    const s = pc.iceConnectionState;
    console.log(`[PC] 🧊 ICE ${device_id}: ${s}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🧊 ICE ${device_id}: ${s}`, deviceId: device_id });
    const existing = refs.remoteDeviceInfoRef.current.get(device_id) || { device_id, label: device_id };
    refs.remoteDeviceInfoRef.current.set(device_id, { ...existing, iceState: s });
  };

  pc.onicegatheringstatechange = () => {
    console.log(`[PC] 🧊 ICE gathering ${device_id}: ${pc.iceGatheringState}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🧊 ICE gathering ${device_id}: ${pc.iceGatheringState}`, deviceId: device_id });
  };

  pc.onsignalingstatechange = () => {
    console.log(`[PC] 📶 Signaling ${device_id}: ${pc.signalingState}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `📶 Signaling ${device_id}: ${pc.signalingState}`, deviceId: device_id });
  };

  return pc;
};


// ============================================================================
// SECTION 13 — Offer régie → device
// ============================================================================

export const connectToDevice = async (
  device_id: string,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
  onWatchdogRetry: (device_id: string) => Promise<void>
): Promise<void> => {

  if (negotiationsInProgress.get(device_id)) {
    console.warn(`[PC] ⏭ connectToDevice ${device_id} ignoré — négociation déjà en cours (mutex)`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⏭ connectToDevice ${device_id} ignoré — négociation déjà en cours`, deviceId: device_id });
    return;
  }
  negotiationsInProgress.set(device_id, true);
  // Génération à laquelle cette négociation appartient : si le device annonce
  // une nouvelle session avant qu'elle n'aboutisse, elle devient caduque.
  const generation = negotiationGeneration.get(device_id) ?? 0;

  try {
    const existing = refs.remoteConnectionsRef.current.get(device_id);
    const state    = existing?.peerConnection?.connectionState;

    // « Déjà connecté » ne vaut que pour la session EN COURS. Une PC née d'une
    // session antérieure du device est un fantôme : elle se dira `connected`
    // encore une dizaine de secondes après le départ de son correspondant.
    const heritee = existing !== undefined && pcGeneration.get(device_id) !== generation;

    if (!heritee && (state === 'connected' || state === 'connecting')) {
      console.warn(`[PC] ⏭ connectToDevice ${device_id} ignoré (état: ${state})`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⏭ connectToDevice ${device_id} ignoré (état: ${state})`, deviceId: device_id });
      return;
    }

    if (heritee) {
      console.warn(`[PC] 👻 ${device_id}: PC héritée de la session précédente (état: ${state}) — on renégocie`);
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `👻 PC héritée de la session précédente (état: ${state}) — renégociation`,
        deviceId: device_id,
      });
    }

    if (existing) {
      console.warn(`[PC] 🔄 ${device_id}: fermeture PC existante (état: ${state}) avant reconnexion`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔄 Fermeture PC existante (état: ${state}) avant reconnexion`, deviceId: device_id });
      notePcCloseCause(device_id, `connectToDevice — reconnexion, PC existante fermée (état: ${state ?? 'null'})`);
      existing.peerConnection.close();
      cleanupDeviceBuffers(device_id, refs);
    }

    console.log(`[PC] ▶ Début négociation → ${device_id}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `▶ Début négociation → ${device_id}`, deviceId: device_id });

    const pc = makePeerConnection({ device_id, refs, callbacks, onWatchdogRetry });

    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });

    callbacks.setRemoteConnections(prev => {
      const next = new Map(prev);
      next.set(device_id, { device_id, peerConnection: pc, stream: null });
      return next;
    });
    refs.remoteConnectionsRef.current.set(device_id, { device_id, peerConnection: pc, stream: null });
    pcGeneration.set(device_id, generation);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Attendre ICE gathering (max 6s)
    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 6000);
    });

    // Le gathering a pu durer 6 s : le device a eu le temps de repartir et de
    // revenir, et une négociation neuve est en route pour sa nouvelle session.
    // Offrir maintenant, c'est offrir depuis une PC que `cleanupDeadConnection`
    // a fermée, pour une session qui n'existe plus — et écraser au passage la
    // négociation qui, elle, est valable.
    const perimee = (negotiationGeneration.get(device_id) ?? 0) !== generation;
    if (perimee || pc.signalingState === 'closed' || !pc.localDescription) {
      const motif = perimee
        ? 'le device a annoncé une nouvelle session'
        : 'la PeerConnection a été fermée pendant le gathering';
      console.warn(`[PC] 🚫 Offer abandonnée pour ${device_id} — ${motif}`);
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `🚫 Offer abandonnée — ${motif}`,
        deviceId: device_id,
      });
      try { pc.close(); } catch { /* déjà fermée */ }
      return;
    }

    console.log(`[PC] 🧊 ICE gathering terminé (${pc.iceGatheringState}) → envoi Offer à ${device_id}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🧊 ICE gathering terminé (${pc.iceGatheringState}) → envoi Offer`, deviceId: device_id });

    const finalSdp = forceSdpCodecs(pc.localDescription!.sdp);
    const sdpSummary = summarizeSdp(finalSdp, `Offer → ${device_id}`);
    console.log(`[PC] ↑ ${sdpSummary}`);
    callbacks.addSdpLog({ direction: 'sent', type: 'offer', label: sdpSummary, deviceId: device_id, sdp: finalSdp, codec: parseSdpCodec(finalSdp) });

    if (refs.wsRef.current?.readyState === WebSocket.OPEN) {
      const msg: any = { type: 'offer', target: device_id, payload: { type: 'offer', sdp: finalSdp } };
      if (refs.requiredCodecRef.current) {
        msg.required_codec = refs.requiredCodecRef.current;
        console.log(`[PC] 🔒 Codec imposé dans l'Offer: ${refs.requiredCodecRef.current.codec} ${refs.requiredCodecRef.current.profile}`);
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔒 Codec imposé: ${refs.requiredCodecRef.current.codec} ${refs.requiredCodecRef.current.profile}`, deviceId: device_id });
      }
      refs.wsRef.current.send(JSON.stringify(msg));
      console.log(`[PC] ↑ Offer envoyée à ${device_id} (SDP ${finalSdp.length} chars)`);
    } else {
      console.error(`[PC] ❌ WS fermé — impossible d'envoyer l'Offer à ${device_id}`);
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: `❌ WS fermé — impossible d'envoyer l'Offer à ${device_id}`, deviceId: device_id });
    }

  } finally {
    // Ne libérer QUE son propre mutex : s'il a été repris par une négociation
    // plus récente (supplantation), l'effacer ici en autoriserait une troisième
    // en parallèle.
    if ((negotiationGeneration.get(device_id) ?? 0) === generation) {
      negotiationsInProgress.delete(device_id);
    }
  }
};

// ============================================================================
// SECTION 14 — Réception Offer device → régie
// Perfect Negotiation : la régie est IMPOLITE
// ============================================================================

export const handleRemoteOffer = async (
  device_id: string,
  offer: RTCSessionDescriptionInit,
  meta: { supported_codecs?: string[] } | undefined,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
  remoteDevices: { device_id: string; label: string }[],
  onWatchdogRetry: (device_id: string) => Promise<void>
): Promise<void> => {

  const offerSummary = summarizeSdp(offer.sdp, `Offer ← ${device_id}`);
  console.log(`[PC] ↓ ${offerSummary}`);
  callbacks.addSdpLog({ direction: 'received', type: 'offer', label: offerSummary, deviceId: device_id, sdp: offer.sdp, codec: offer.sdp ? parseSdpCodec(offer.sdp) : undefined, raw: { supported_codecs: meta?.supported_codecs } });

  if (meta?.supported_codecs) {
    refs.deviceSupportedCodecsRef.current.set(device_id, meta.supported_codecs);
    console.log(`[PC] 📋 Codecs ${device_id}: ${meta.supported_codecs.join(', ')}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `Codecs ${device_id}: ${meta.supported_codecs.join(', ')}`, deviceId: device_id });
  }

  const existingForCheck = refs.remoteConnectionsRef.current.get(device_id);
  if (existingForCheck?.peerConnection?.signalingState === 'have-local-offer') {
    console.warn(`[PC] ⚠️ handleRemoteOffer ${device_id} ignoré — régie impolite (signalingState=have-local-offer)`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `⚠️ Offer ← ${device_id} ignorée — régie impolite (have-local-offer en cours)`,
      deviceId: device_id,
    });
    return;
  }

  const required = refs.requiredCodecRef.current;
  if (required) {
    const supportedCodecs = refs.deviceSupportedCodecsRef.current.get(device_id) || [];
    const supportsRequired = supportedCodecs.some(c => c.toLowerCase().includes(required.codec.toLowerCase()));
    if (supportedCodecs.length > 0 && !supportsRequired) {
      console.warn(`[PC] ❌ ${device_id} rejeté: codec ${required.codec} non supporté (supportés: ${supportedCodecs.join(', ')})`);
      callbacks.addSdpLog({ direction: 'received', type: 'error', label: `❌ ${device_id} rejeté — ${required.codec} non supporté`, deviceId: device_id, raw: { required, supported: supportedCodecs } });
      if (refs.wsRef.current?.readyState === WebSocket.OPEN) {
        refs.wsRef.current.send(JSON.stringify({ type: 'device_rejected', target: device_id, reason: 'codec_incompatible', required_codec: required, supported_codecs: supportedCodecs }));
      }
      return;
    }
  }

  const existing = refs.remoteConnectionsRef.current.get(device_id);
  if (existing) {
    console.warn(`[PC] 🔄 handleRemoteOffer: PC existante pour ${device_id} (état: ${existing.peerConnection.connectionState}) → fermeture`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `🔄 handleRemoteOffer: PC existante fermée (état: ${existing.peerConnection.connectionState})`, deviceId: device_id });
    notePcCloseCause(device_id, `handleRemoteOffer — Offer réémise par le device, PC existante fermée (état: ${existing.peerConnection.connectionState})`);
    existing.peerConnection.close();
    cleanupDeviceBuffers(device_id, refs);
  }

  const pc = makePeerConnection({ device_id, refs, callbacks, onWatchdogRetry });
  callbacks.setRemoteConnections(prev => {
    const next = new Map(prev);
    next.set(device_id, { device_id, peerConnection: pc, stream: null });
    return next;
  });
  refs.remoteConnectionsRef.current.set(device_id, { device_id, peerConnection: pc, stream: null });

  if (offer.sdp) {
    const codecs = extractCodecFromSdp(offer.sdp);
    const device = remoteDevices.find(d => d.device_id === device_id);
    const ex     = refs.remoteDeviceInfoRef.current.get(device_id) || { device_id, label: device?.label || device_id };
    refs.remoteDeviceInfoRef.current.set(device_id, { ...ex, lastOfferSdp: offer.sdp, negotiatedVideoCodec: codecs.videoCodec || ex.negotiatedVideoCodec, negotiatedVideoProfile: codecs.videoProfile || ex.negotiatedVideoProfile, negotiatedAudioCodec: codecs.audioCodec || ex.negotiatedAudioCodec });
  }

  await pc.setRemoteDescription(offer);
  refs.remoteDescSetRef.current.set(device_id, true);
  await flushIceCandidateBuffer(device_id, pc, refs, callbacks);

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  console.log(`[PC] ↑ Answer envoyée à ${device_id}`);
  if (answer.sdp) {
    const answerSummary = summarizeSdp(answer.sdp, `Answer → ${device_id}`);
    console.log(`[PC] ↑ ${answerSummary}`);
    callbacks.addSdpLog({ direction: 'sent', type: 'answer', label: answerSummary, deviceId: device_id, sdp: answer.sdp, codec: parseSdpCodec(answer.sdp) });
    const ex = refs.remoteDeviceInfoRef.current.get(device_id) || { device_id, label: device_id };
    refs.remoteDeviceInfoRef.current.set(device_id, { ...ex, lastAnswerSdp: answer.sdp });
  }

  if (refs.wsRef.current?.readyState === WebSocket.OPEN) {
    refs.wsRef.current.send(JSON.stringify({ type: 'answer', target: device_id, payload: answer }));
  }
};

// ============================================================================
// SECTION 15 — Réception Answer device → régie
// ============================================================================

export const handleRemoteAnswer = async (
  device_id: string,
  answer: RTCSessionDescriptionInit,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks
): Promise<void> => {
  const conn = refs.remoteConnectionsRef.current.get(device_id);
  if (!conn) {
    console.warn(`[PC] handleRemoteAnswer: pas de PeerConnection pour ${device_id}`);
    return;
  }

  if (conn.peerConnection.signalingState !== 'have-local-offer') {
    console.warn(`[PC] ⚠️ handleRemoteAnswer ${device_id} ignorée — signalingState=${conn.peerConnection.signalingState} (attendu: have-local-offer)`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `⚠️ Answer ← ${device_id} ignorée (signalingState=${conn.peerConnection.signalingState} — Answer orpheline ?)`, deviceId: device_id });
    return;
  }

  console.log(`[PC] ↓ Answer reçue de ${device_id}`);
  if (answer.sdp) {
    const answerSummary = summarizeSdp(answer.sdp, `Answer ← ${device_id}`);
    console.log(`[PC] ↓ ${answerSummary}`);
    callbacks.addSdpLog({ direction: 'received', type: 'answer', label: answerSummary, deviceId: device_id, sdp: answer.sdp, codec: parseSdpCodec(answer.sdp) });
  }

  await conn.peerConnection.setRemoteDescription(answer);
  refs.remoteDescSetRef.current.set(device_id, true);
  await flushIceCandidateBuffer(device_id, conn.peerConnection, refs, callbacks);

  if (answer.sdp) {
    const codecs = extractCodecFromSdp(answer.sdp);
    const ex     = refs.remoteDeviceInfoRef.current.get(device_id) || { device_id, label: device_id };
    refs.remoteDeviceInfoRef.current.set(device_id, { ...ex, lastAnswerSdp: answer.sdp, negotiatedVideoCodec: codecs.videoCodec || ex.negotiatedVideoCodec, negotiatedVideoProfile: codecs.videoProfile || ex.negotiatedVideoProfile, negotiatedAudioCodec: codecs.audioCodec || ex.negotiatedAudioCodec });
  }
};

// ============================================================================
// SECTION 16 — Candidat ICE entrant
// ============================================================================

export const handleRemoteIceCandidate = async (
  device_id: string,
  candidate: RTCIceCandidateInit,
  refs: SignalingRefs
): Promise<void> => {
  const summary = summarizeIceCandidate(candidate);
  const conn = refs.remoteConnectionsRef.current.get(device_id);

  if (!conn) {
    const buf = refs.iceCandidateBufferRef.current.get(device_id) ?? [];
    buf.push(candidate);
    refs.iceCandidateBufferRef.current.set(device_id, buf);
    console.log(`[PC] 🧊 ICE bufférisé pour ${device_id} (pas de PC) [${summary}] — buffer: ${buf.length}`);
    return;
  }

  const remoteDescReady = refs.remoteDescSetRef.current.get(device_id) ?? false;
  if (!remoteDescReady) {
    const buf = refs.iceCandidateBufferRef.current.get(device_id) ?? [];
    buf.push(candidate);
    refs.iceCandidateBufferRef.current.set(device_id, buf);
    console.log(`[PC] 🧊 ICE bufférisé pour ${device_id} (remoteDesc pas prête) [${summary}] — buffer: ${buf.length}`);
    return;
  }

  console.log(`[PC] 🧊 ICE entrant appliqué pour ${device_id}: ${summary}`);
  try {
    await conn.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
  } catch (err) {
    console.warn(`[PC] ⚠ Erreur addIceCandidate ${device_id} (${summary}):`, err);
  }
};

// ============================================================================
// SECTION 17 — Flux retour régie → device (canvasStreamRef → device)
// ============================================================================

const returnPeerConnections = new Map<string, RTCPeerConnection>();

// ── Plafond d'encodage du retour ──────────────────────────────────────────────
//
// Le retour partait SANS AUCUN plafond : `pc.addTrack()` puis rien. Le téléphone
// recevait donc le canvas du programme dans sa définition native — 1920×1080 — et
// Chrome poussait jusqu'à ce que le lien casse.
//
// Mesuré le 25/08/2026 sur Nasa TV, sur 46 relevés de l'onglet Débits : les DEUX
// téléphones présents recevaient ~2,5 Mb/s de retour, et Chrome déclarait les deux
// limités par la bande passante — « alex » 6 fois (rabaissé jusqu'à 480×270), « er »
// 3 fois (jusqu'à 640×360). Ce n'était donc pas le cas particulier d'un appareil.
//
// Sur une radio mobile, montant et descendant partagent le même lien : un descendant
// saturé retarde les paquets RTP montants du téléphone et ses retours RTCP. La régie
// voit alors le flux montant caler, déclare la connexion morte, et le cycle
// recommence — c'est la boucle de reconnexion qui a fait perdre son angle à « alex »
// sur trois prises d'affilée.
//
// Chrome dégradait déjà tout seul, mais RÉACTIVEMENT : il sature d'abord, constate,
// recule, recommence. Le plafond explicite supprime le dépassement au lieu de le
// corriger après coup — c'est là qu'est le gain, pas dans le débit moyen.
//
// ⚠️ `maintain-framerate` est l'INVERSE du réglage d'un angle (cf. api_recordingAngles),
// et c'est voulu : un angle est une archive qu'on regardera en grand, un retour sert à
// CADRER. L'opérateur a besoin d'un geste fluide, pas de pixels — et l'écran d'un
// téléphone ne restitue de toute façon pas le 1080p.
const RETURN_MAX_BITRATE   = 800_000;
const RETURN_TARGET_WIDTH  = 640;  // largeur visée du retour, quelle que soit la chaîne
const RETURN_MAX_FRAMERATE = 20;

/**
 * Pose le plafond d'encodage sur la piste vidéo du retour. Sans effet si le navigateur
 * ne supporte pas `setParameters` — on publie alors comme avant, sans plafond.
 *
 * Le diviseur est CALCULÉ sur la largeur réelle du canvas, jamais figé : un ÷3 posé en
 * dur donne 640×360 sur une chaîne en 1080p mais 426×240 sur une chaîne en 720p, où le
 * retour deviendrait inutilisable pour cadrer. On vise une largeur, pas un rapport.
 */
const applyReturnEncoding = async (sender: RTCRtpSender, device_id: string): Promise<void> => {
  try {
    const largeurSource = sender.track?.getSettings().width ?? 0;
    // Largeur inconnue (certains navigateurs ne la renseignent pas sur une piste de
    // canvas) : on ne réduit pas plutôt que de réduire au hasard. Le débit et la
    // cadence plafonnent de toute façon.
    const diviseur = largeurSource > RETURN_TARGET_WIDTH
      ? largeurSource / RETURN_TARGET_WIDTH
      : 1;

    const params = sender.getParameters();
    if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
    params.encodings[0].maxBitrate            = RETURN_MAX_BITRATE;
    params.encodings[0].scaleResolutionDownBy = diviseur;
    params.encodings[0].maxFramerate          = RETURN_MAX_FRAMERATE;
    params.degradationPreference              = 'maintain-framerate';
    await sender.setParameters(params);
    console.log(`[PC-RETURN] 🎚 Plafond retour → ${device_id}: ${RETURN_MAX_BITRATE / 1000} kb/s, ${RETURN_MAX_FRAMERATE} i/s, ${largeurSource || '?'}px ÷${diviseur.toFixed(2)}`);
  } catch (err) {
    console.warn(`[PC-RETURN] ⚠ Plafond retour non appliqué pour ${device_id} — le retour part sans limite :`, err);
  }
};

/**
 * Devices dont le flux retour n'a pas pu être établi et reste à reprendre.
 *
 * Le retour n'est tenté qu'UNE fois, 500 ms après que la régie se connecte au
 * device (useLiveStreamSetup). Quand le canvas de la régie n'existe pas encore à
 * cet instant — typiquement au redémarrage d'une chaîne, où le device se
 * reconnecte avant que la régie ne compose quoi que ce soit — l'appel repartait
 * sur un log « report » que rien ne reprenait : l'appareil restait sans retour
 * jusqu'à ce qu'il se reconnecte de lui-même. Constaté le 14/08/2026 sur
 * Lovelight TV, après extinction générale puis réactivation de la chaîne.
 *
 * Le device est retenu ici, et retryPendingReturnStreams le rejoue tant que la
 * cause est passagère. Il en sort sur succès, ou quand la régie l'oublie
 * (closeReturnStreamToDevice).
 */
const pendingReturnDevices = new Set<string>();

/**
 * Devices dont une tentative de retour est en cours — mutex, pendant SDP.
 *
 * Sans lui, deux tentatives peuvent se chevaucher, et le retour est alors perdu
 * pour de bon. Deux chemins les produisent :
 *
 *  - le ticker de reprise bat toutes les secondes, alors qu'une tentative dure
 *    jusqu'à 6 s (attente du gathering ICE, TURN compris) et que le device n'est
 *    retiré de pendingReturnDevices qu'à la toute fin ;
 *  - connectToDeviceImpl programme un retour 500 ms après CHAQUE appel, y compris
 *    ceux que le mutex de négociation `negotiationsInProgress` a laissés passer
 *    sans rien faire (device_connected puis device_list, par exemple).
 *
 * Le mécanisme du dégât : chaque tentative ferme la PC de la précédente et prend
 * sa place dans returnPeerConnections — mais une PC fermée garde sa
 * localDescription, donc l'ancienne tentative finit par envoyer SON Offer, après
 * la nouvelle. Le device répond dans l'ordre où il a reçu les Offers, et
 * handleReturnAnswer applique ces Answers à la dernière PC : empreinte DTLS qui
 * ne correspond pas, ou setRemoteDescription en état `stable`. L'appareil reçoit
 * bien des Offers, répond, et n'a jamais d'image.
 */
const returnNegotiations = new Set<string>();

/** Y a-t-il un flux retour en attente ? Permet à l'appelant d'éviter de
 *  construire refs et callbacks pour un tick à vide. */
export const hasPendingReturnStreams = (): boolean => pendingReturnDevices.size > 0;

/**
 * Rejoue les retours en attente. Sans effet tant que le canvas manque : c'est
 * précisément ce qu'on attend, inutile de reconstruire une PC pour rien.
 *
 * Renvoie le nombre de retours rétablis, pour que l'appelant puisse le tracer.
 */
export const retryPendingReturnStreams = async (
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
): Promise<number> => {
  if (pendingReturnDevices.size === 0)  return 0;
  if (!refs.canvasStreamRef.current)    return 0;

  let restored = 0;
  // Copie : sendReturnStreamToDevice modifie l'ensemble en cours de route.
  for (const device_id of Array.from(pendingReturnDevices)) {
    if (await sendReturnStreamToDevice(device_id, refs, callbacks)) restored++;
  }
  return restored;
};

/**
 * Établit le flux retour vers un device. Renvoie `false` si le retour n'a pas pu
 * partir — le device est alors retenu dans pendingReturnDevices, et la reprise
 * périodique s'en chargera.
 *
 * Appel réentrant sans danger : une tentative déjà en cours pour ce device rend
 * la main immédiatement, et un retour déjà établi n'est pas renégocié.
 */
export const sendReturnStreamToDevice = async (
  device_id: string,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
): Promise<boolean> => {
  // Une tentative à la fois par device. On ne touche pas à pendingReturnDevices
  // ici : la tentative en cours porte déjà l'issue de celle-ci — elle retirera le
  // device de la file si elle aboutit, l'y remettra sinon.
  if (returnNegotiations.has(device_id)) {
    console.log(`[PC-RETURN] ⏭ Retour ${device_id} ignoré — tentative déjà en cours`);
    return false;
  }

  const canvasStream = refs.canvasStreamRef.current;
  if (!canvasStream) {
    // Journalisé une seule fois : la reprise repasse ici chaque seconde, et
    // inonder le journal SDP masquerait tout le reste de la négociation.
    if (!pendingReturnDevices.has(device_id)) {
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `↩ sendReturnStreamToDevice ${device_id}: canvasStream non disponible — report`,
        deviceId: device_id,
      });
      pendingReturnDevices.add(device_id);
    }
    return false;
  }

  // Retour déjà en place : ne pas le démolir pour le refaire. connectToDeviceImpl
  // programme un retour 500 ms après chaque appel, et le ticker de reprise peut
  // repasser ici — sans ce garde, une PC qui fonctionne serait fermée puis
  // renégociée, avec la coupure d'image que cela suppose.
  const established = returnPeerConnections.get(device_id);
  if (established && (established.connectionState === 'connected' || established.connectionState === 'connecting')) {
    console.log(`[PC-RETURN] ⏭ Retour ${device_id} déjà ${established.connectionState} — conservé`);
    pendingReturnDevices.delete(device_id);
    return true;
  }

  returnNegotiations.add(device_id);
  try {
    // forgetPending: false — cette fermeture prépare une nouvelle tentative, elle
    // ne signifie pas que la régie renonce au retour de cet appareil.
    closeReturnStreamToDevice(device_id, callbacks, { forgetPending: false });

    console.log(`[PC-RETURN] ▶ Flux retour → ${device_id}`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `↩ Flux retour : création PC → ${device_id}`, deviceId: device_id });

    const pc = new RTCPeerConnection(defaultRTCConfig());
    trackPeerConnection(pc, 'retour', device_id);
    returnPeerConnections.set(device_id, pc);
    // Et tout retour de cet appareil qui aurait perdu sa place dans la Map : il
    // encoderait le canvas une seconde fois, en pure perte de lien montant.
    closeStrayPeerConnections('retour', device_id, pc);

    const audioStream    = refs.audioMixerDestRef.current?.stream ?? null;
    const combinedStream = new MediaStream();

    // Les senders sont retenus pour recevoir le plafond juste après : `setParameters`
    // est asynchrone, il n'a pas sa place dans le forEach.
    const returnVideoSenders: RTCRtpSender[] = [];
    canvasStream.getVideoTracks().forEach(track => {
      combinedStream.addTrack(track);
      returnVideoSenders.push(pc.addTrack(track, combinedStream));
      console.log(`[PC-RETURN] 🎬 Track ajouté (video) → ${device_id}`);
    });

    // AVANT createOffer : les paramètres posés après la négociation ne prennent effet
    // qu'au renégociation suivante, et le retour partirait une fois de plus en 1080p.
    for (const sender of returnVideoSenders) {
      await applyReturnEncoding(sender, device_id);
    }

    if (audioStream) {
      audioStream.getAudioTracks().forEach(track => {
        combinedStream.addTrack(track);
        pc.addTrack(track, combinedStream);
        console.log(`[PC-RETURN] 🎬 Track ajouté (audio) → ${device_id}`);
      });
    } else {
      console.warn(`[PC-RETURN] ⚠ audioMixerDestRef vide — pas d'audio retour pour ${device_id}`);
    }

    pc.onicecandidate = (event) => {
      if (event.candidate && refs.wsRef.current?.readyState === WebSocket.OPEN) {
        const summary = summarizeIceCandidate(event.candidate);
        console.log(`[PC-RETURN] 🧊 ICE sortant → ${device_id}: ${summary}`);
        callbacks.addSdpLog({ direction: 'sent', type: 'event', label: `↩ ICE retour → ${device_id}: ${summary}`, deviceId: device_id });
        refs.wsRef.current.send(JSON.stringify({
          type      : 'ice-candidate',
          target    : device_id,
          stream_id : 'return',
          payload   : event.candidate,
        }));
      } else if (!event.candidate) {
        console.log(`[PC-RETURN] 🧊 ICE gathering terminé pour ${device_id}`);
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      console.log(`[PC-RETURN] 🔗 ${device_id}: ${state}`);
      callbacks.addSdpLog({
        direction : state === 'failed' || state === 'disconnected' ? 'received' : 'info',
        type      : state === 'failed' || state === 'disconnected' ? 'error'    : 'event',
        label     : `↩ Retour ${device_id}: ${state}`,
        deviceId  : device_id,
      });
      // `failed` : la connexion ne se rétablira pas d'elle-même (il faudrait un
      // redémarrage ICE, et le retour se recrée plutôt). On la FERME : la
      // retirer de la Map sans la fermer, comme avant, la laissait ouverte vers
      // le relais TURN, hors de portée de tout nettoyage — une de plus à chaque
      // veille ou changement de réseau d'un téléphone (les 120 connexions TURN
      // du 05/10/2026).
      //
      // `disconnected` : elle peut revenir seule ; on la laisse dans la Map.
      // Si un nouveau retour est négocié entre-temps, c'est
      // closeReturnStreamToDevice qui la fermera — elle doit donc y rester.
      //
      // Toujours vérifier que la Map désigne encore CETTE connexion : un nouveau
      // retour a pu la remplacer, et ce n'est pas à l'ancienne de le retirer.
      if (state === 'failed') {
        notePcCloseCause(device_id, 'retour en échec (failed) — fermé');
        try { pc.close(); } catch { /* déjà fermée */ }
      }
      if ((state === 'failed' || state === 'closed') && returnPeerConnections.get(device_id) === pc) {
        returnPeerConnections.delete(device_id);
      }
    };

    pc.onicegatheringstatechange = () => {
      console.log(`[PC-RETURN] 🧊 ICE gathering ${device_id}: ${pc.iceGatheringState}`);
      callbacks.addSdpLog({ direction: 'info', type: 'event', label: `↩ ICE gathering retour ${device_id}: ${pc.iceGatheringState}`, deviceId: device_id });
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 6000);
    });

    // Cette PC est-elle toujours celle du device ? L'attente ci-dessus dure jusqu'à
    // 6 s, pendant lesquelles la régie a pu fermer le retour (déconnexion,
    // nettoyage). Une PC fermée garde sa localDescription : sans ce garde, on
    // enverrait quand même l'Offer d'une PC morte, et l'Answer du device viendrait
    // s'appliquer sur une autre PeerConnection. On ne remet pas le device en
    // attente — s'il y a une raison de réessayer, c'est à celui qui a fermé de la
    // porter.
    if (returnPeerConnections.get(device_id) !== pc || pc.signalingState !== 'have-local-offer') {
      console.warn(`[PC-RETURN] ⏭ Offer retour ${device_id} abandonnée — PC périmée (état: ${pc.signalingState})`);
      callbacks.addSdpLog({
        direction: 'info', type: 'event',
        label: `↩ Offer retour abandonnée pour ${device_id} — PC périmée (${pc.signalingState})`,
        deviceId: device_id,
      });
      return false;
    }

    if (refs.wsRef.current?.readyState !== WebSocket.OPEN) {
      console.error(`[PC-RETURN] ❌ WS fermé — impossible d'envoyer l'Offer retour à ${device_id}`);
      // Retenu comme le cas du canvas absent : une WS fermée se rouvre, et le
      // retour doit repartir à ce moment-là plutôt que d'être perdu.
      if (!pendingReturnDevices.has(device_id)) {
        callbacks.addSdpLog({ direction: 'received', type: 'error', label: `❌ WS fermé — Offer retour non envoyée à ${device_id}`, deviceId: device_id });
        pendingReturnDevices.add(device_id);
      }
      return false;
    }

    const sdp = pc.localDescription!.sdp;
    const sdpSummary = summarizeSdp(sdp, `Offer retour → ${device_id}`);
    console.log(`[PC-RETURN] ↑ ${sdpSummary}`);
    callbacks.addSdpLog({ direction: 'sent', type: 'offer', label: `↩ ${sdpSummary}`, deviceId: device_id, sdp, codec: parseSdpCodec(sdp) });

    refs.wsRef.current.send(JSON.stringify({
      type      : 'offer',
      target    : device_id,
      stream_id : 'return',
      payload   : { type: 'offer', sdp },
    }));

    pendingReturnDevices.delete(device_id);
    console.log(`[PC-RETURN] ↑ Offer retour envoyée à ${device_id}`);
    return true;

  } finally {
    returnNegotiations.delete(device_id);
  }
};

export const handleReturnAnswer = async (
  device_id: string,
  answer: RTCSessionDescriptionInit,
  callbacks: SignalingCallbacks,
): Promise<void> => {
  const pc = returnPeerConnections.get(device_id);
  if (!pc) {
    console.warn(`[PC-RETURN] handleReturnAnswer: pas de PC retour pour ${device_id}`);
    return;
  }
  // Une Answer ne s'applique qu'à la PC qui a émis l'Offer correspondante, et
  // seulement si celle-ci attend encore une réponse. Une PC en `stable` a déjà la
  // sienne : l'Answer qui arrive est celle d'une tentative périmée, l'appliquer
  // lèverait InvalidStateError — et pire, sur une PC neuve elle passerait avec une
  // empreinte DTLS étrangère, laissant un retour qui ne se connecte jamais.
  if (pc.signalingState !== 'have-local-offer') {
    console.warn(`[PC-RETURN] ⏭ Answer retour ignorée pour ${device_id} — PC en état ${pc.signalingState}`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `↩ Answer retour ignorée pour ${device_id} — PC en état ${pc.signalingState}`,
      deviceId: device_id,
    });
    return;
  }

  console.log(`[PC-RETURN] ↓ Answer retour ← ${device_id}`);
  const answerSummary = summarizeSdp(answer.sdp, `Answer retour ← ${device_id}`);
  callbacks.addSdpLog({ direction: 'received', type: 'answer', label: `↩ ${answerSummary}`, deviceId: device_id, sdp: answer.sdp, codec: answer.sdp ? parseSdpCodec(answer.sdp) : undefined });
  try {
    await pc.setRemoteDescription(answer);
  } catch (err: any) {
    console.error(`[PC-RETURN] ❌ setRemoteDescription retour ${device_id}:`, err);
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: `❌ Answer retour refusée par ${device_id}: ${err?.message ?? err}`, deviceId: device_id });
  }
};

export const handleReturnIceCandidate = async (
  device_id: string,
  candidate: RTCIceCandidateInit,
): Promise<void> => {
  const pc = returnPeerConnections.get(device_id);
  if (!pc) {
    console.warn(`[PC-RETURN] handleReturnIceCandidate: pas de PC retour pour ${device_id}`);
    return;
  }
  const summary = summarizeIceCandidate(candidate);
  console.log(`[PC-RETURN] 🧊 ICE retour entrant ← ${device_id}: ${summary}`);
  try {
    await pc.addIceCandidate(new RTCIceCandidate(candidate));
  } catch (err) {
    console.warn(`[PC-RETURN] ⚠ Erreur addIceCandidate retour ${device_id} (${summary}):`, err);
  }
};

export const closeReturnStreamToDevice = (
  device_id: string,
  callbacks?: Pick<SignalingCallbacks, 'addSdpLog'>,
  /**
   * Par défaut, fermer le retour signifie que la régie renonce à cet appareil
   * (déconnexion, nettoyage) : il sort donc de la file de reprise. Seule une
   * fermeture qui prépare une nouvelle tentative passe `false`.
   */
  options?: { forgetPending?: boolean },
): void => {
  if (options?.forgetPending !== false) pendingReturnDevices.delete(device_id);
  const pc = returnPeerConnections.get(device_id);
  if (pc) {
    pc.close();
    returnPeerConnections.delete(device_id);
    callbacks?.addSdpLog?.({ direction: 'info', type: 'event', label: `↩ PC retour fermée pour ${device_id}`, deviceId: device_id });
    console.log(`[PC-RETURN] PC retour fermée pour ${device_id}`);
  }
};

/**
 * Ferme TOUS les flux retour, et renvoie combien l'étaient.
 *
 * À appeler au démontage de la régie. Le retour est composé par le canvas de
 * CETTE régie : dès qu'elle disparaît, `stopCompositingLoop` cesse d'y dessiner.
 * Mais rien d'autre ne s'arrête — la piste de `canvas.captureStream()` reste
 * `live`, et ces PeerConnections vivent dans un Map de MODULE, qui survit au
 * démontage. Le device continue donc de recevoir un flux parfaitement sain,
 * simplement immobile : **image figée sur la dernière frame composée, sans fin.**
 *
 * Et le retour sur la chaîne n'y changeait rien : la nouvelle régie recrée son
 * canvas, mais `sendReturnStreamToDevice` voit une PC encore `connected` et la
 * conserve — celle qui est branchée sur l'ancien canvas, mort.
 *
 * Fermer est donc le seul comportement honnête : sans régie affichée, personne
 * ne compose ce retour. Le device voit sa PC tomber au lieu de croire à une
 * image, et le retour est renégocié au retour sur la chaîne, sur le canvas
 * courant. Les caméras montantes, elles, ne sont PAS touchées : elles n'ont
 * aucune raison de se couper quand l'opérateur change d'onglet.
 */
export const closeAllReturnStreams = (
  callbacks?: Pick<SignalingCallbacks, 'addSdpLog'>,
): number => {
  const ids = Array.from(returnPeerConnections.keys());
  ids.forEach(device_id => closeReturnStreamToDevice(device_id, callbacks));
  // La file de reprise ne doit pas survivre non plus : le ticker qui la rejoue
  // vit dans la régie qu'on démonte, et un device qui y resterait serait rejoué
  // par la régie SUIVANTE — donc sur le programme d'une autre chaîne.
  pendingReturnDevices.clear();
  if (ids.length) console.log(`[PC-RETURN] ⏹ ${ids.length} flux retour fermé(s) — régie démontée`);
  return ids.length;
};